import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = getCategoryBySlug(slug || '');
  const categoryArticles = getArticlesByCategory(slug || '');

  const colorMap: Record<string, string> = {
    primary: 'bg-primary-500',
    accent: 'bg-accent-500',
    secondary: 'bg-secondary-500',
  };

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory?.name || '赛事分类'} - 每日大赛`,
          url: `https://dairise.github.io/category/${slug}`,
        }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory?.name || '赛事分类' },
              ]}
            />

            <div className="flex items-center gap-4 mb-2">
              {currentCategory && (
                <div
                  className={`w-12 h-12 rounded-xl ${colorMap[currentCategory.color] || colorMap.primary} flex items-center justify-center`}
                >
                  <i className={`${currentCategory.icon} text-background-50 text-xl`}></i>
                </div>
              )}
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {currentCategory?.name || '赛事分类'}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {currentCategory?.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-60 flex-shrink-0">
              <nav className="sticky top-24">
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">
                  全部类别
                </h3>
                <ul className="space-y-1">
                  {categories.map((cat) => (
                    <li key={cat.slug}>
                      <Link
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap ${
                          cat.slug === slug
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                        }`}
                      >
                        <i className={`${cat.icon} text-base`}></i>
                        <span>{cat.name}</span>
                        <span className="ml-auto text-xs text-foreground-400">{cat.count}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            <div className="flex-1">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group block bg-background-50 rounded-xl border border-background-200/60 overflow-hidden hover:border-background-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          title={`${article.title} - 每日大赛`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-foreground-400 flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            {article.publishDate}
                          </span>
                          <span className="text-xs text-foreground-400">{article.readTime}分钟阅读</span>
                        </div>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {article.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类下暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}